import {useFormContext} from '@yandex-delivery/form'
import {useEffect, useMemo} from 'react'

import {type Order, CarBodyType} from 'src/shared/types'
import {orderPath} from 'src/shared/utils'

import {getCargoLoadersOptions} from './utils/get-cargo-loaders-options'
import {isCarBodyType} from './utils/is-car-body-type'

const tabTitlesMap = {
  [CarBodyType.Van]: 'S',
  [CarBodyType.LCV_M]: 'M',
  [CarBodyType.LCV_L]: 'L',
  [CarBodyType.LCV_XL]: 'XL',
  [CarBodyType.LCV_XXL]: 'XXL',
}

export function useVerticalCargoRequirements() {
  const {watch, setFieldValue} = useFormContext<Order>()
  const cargoTypeFieldName = orderPath('offerFlow.vertical.requirements.cargo_type.selectedValue')
  const carBodyType = watch(cargoTypeFieldName)
  const carBodyOptions = watch('offerFlow.vertical.requirements.cargo_type.options')
  const carLoadersOptions = watch('offerFlow.vertical.requirements.cargo_loaders.options')
  const selectedLoadersValue = watch('offerFlow.vertical.requirements.cargo_loaders.selectedValue')

  const selectedOption = useMemo(() => {
    return carBodyOptions?.find(option => option.value === carBodyType)
  }, [carBodyOptions, carBodyType])
  const cargoLoadersOptions = getCargoLoadersOptions(selectedOption)

  const preparedCarBodyOptions = useMemo(
    () =>
      carBodyOptions
        ?.map(({value}) => value)
        ?.filter(isCarBodyType)
        ?.map(value => ({
          value,
          label: <span data-e2e={`body-type-${value}`}>{tabTitlesMap[value]}</span>,
        })),
    [carBodyOptions],
  )

  const minCargoLoaders = useMemo(() => {
    const fallbackOptions =
      carLoadersOptions
        ?.map(({value}) => value)
        .filter((value: unknown): value is number => typeof value === 'number') || []

    const minLoaders = Math.min(...(cargoLoadersOptions || fallbackOptions))

    return Number.isFinite(minLoaders) ? minLoaders : 0
  }, [carLoadersOptions, cargoLoadersOptions])

  const maxCargoLoaders = useMemo(() => {
    const fallbackOptions =
      carLoadersOptions
        ?.map(({value}) => value)
        .filter((value: unknown): value is number => typeof value === 'number') || []

    return Math.max(...(cargoLoadersOptions || fallbackOptions))
  }, [carLoadersOptions, cargoLoadersOptions])

  const shouldKeepSelectedLoadersValue =
    selectedLoadersValue !== undefined &&
    selectedLoadersValue >= minCargoLoaders &&
    selectedLoadersValue <= maxCargoLoaders

  /**
   * @BusinessLogic: Если выбранное значение грузчиков не входит в диапазон доступных значений, то устанавливаем минимальное значение
   */
  useEffect(() => {
    if (shouldKeepSelectedLoadersValue) {
      return
    }

    setFieldValue('offerFlow.vertical.requirements.cargo_loaders.selectedValue', minCargoLoaders)
  }, [minCargoLoaders, setFieldValue, shouldKeepSelectedLoadersValue])

  return {
    cargoTypeFieldName,
    carLoadersOptions,
    maxCargoLoaders,
    minCargoLoaders,
    preparedCarBodyOptions,
    selectedOption,
  }
}
